import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarPlus, Info } from 'lucide-react';

import { useMyLeave, useWithdrawLeave, type LeaveBalanceView } from '@frontend/api/hooks/leave';
import { useAuth } from '@frontend/auth/AuthProvider';
import { cn } from '@frontend/lib/cn';
import { formatDate, formatDateRange, humanise } from '@frontend/lib/format';
import { Button } from '@frontend/components/ui/Button';
import { DataTable, type Column } from '@frontend/components/ui/DataTable';
import { Banner, PageHeader } from '@frontend/components/ui/Page';
import { StatusBadge } from '@frontend/components/ui/StatusBadge';
import { EmptyState, ErrorState, LoadingCards } from '@frontend/components/ui/States';
import { useToast } from '@frontend/components/ui/Toast';
import { LedgerDrawer } from './LedgerDrawer';
import type { LeaveRequestView } from '@frontend/api/hooks/leave';

export function MyLeavePage(): JSX.Element {
  const { user } = useAuth();
  const query = useMyLeave();
  const withdraw = useWithdrawLeave();
  const toast = useToast();
  const [ledgerType, setLedgerType] = useState<string | null>(null);

  if (query.isLoading) return <LoadingCards count={4} />;
  if (query.isError) {
    return (
      <>
        <PageHeader title="My leave" />
        <ErrorState
          error={query.error}
          onRetry={() => {
            void query.refetch();
          }}
        />
      </>
    );
  }
  const data = query.data;
  if (data === undefined) return <LoadingCards count={4} />;

  const columns: Column<LeaveRequestView>[] = [
    {
      key: 'type',
      header: 'Type',
      cell: (row) => <span className="font-medium text-content">{humanise(row.leaveType)}</span>,
      sort: (a, b) => a.leaveType.localeCompare(b.leaveType),
    },
    {
      key: 'dates',
      header: 'Dates',
      cell: (row) => (
        <span>
          {formatDateRange(row.fromDate, row.toDate)}
          {row.isHalfDay && (
            <span className="ml-1 text-body text-content-tertiary">
              (half day{row.halfDayPeriod === null ? '' : `, ${row.halfDayPeriod.toLowerCase()}`})
            </span>
          )}
        </span>
      ),
      sort: (a, b) => a.fromDate.localeCompare(b.fromDate),
    },
    { key: 'days', header: 'Days', numeric: true, cell: (row) => row.days, hideOnMobile: true },
    { key: 'status', header: 'Status', cell: (row) => <StatusBadge status={row.status} /> },
    {
      key: 'submitted',
      header: 'Requested',
      hideOnMobile: true,
      cell: (row) => (
        <span className="text-content-tertiary">
          {row.submittedAt == null ? '—' : formatDate(row.submittedAt.slice(0, 10))}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      cell: (row) =>
        ['PENDING', 'PENDING_L1', 'PENDING_L2', 'SUBMITTED'].includes(row.status) ? (
          <Button
            size="xs"
            onClick={() =>
              withdraw.mutate(row.id, {
                onSuccess: () => toast.success('Request withdrawn'),
                onError: (error) => toast.fromError(error, 'Could not withdraw the request'),
              })
            }
            data-testid={`withdraw-${row.id}`}
          >
            Withdraw
          </Button>
        ) : null,
    },
  ];

  return (
    <div>
      <PageHeader
        title="My leave"
        description={`Balances and requests for ${data.year}.`}
        actions={
          <Button
            variant="primary"
            leadingIcon={<CalendarPlus className="h-3.5 w-3.5" />}
            onClick={() => {
              window.location.assign('/leave/request');
            }}
            data-testid="request-leave"
          >
            Request leave
          </Button>
        }
      />

      {!data.eligible && (
        <Banner tone="info" className="mb-4" title="You are not currently eligible for paid leave">
          Your employment type does not accrue leave under the current policy. If you believe that is wrong,
          contact HR — eligibility is a policy setting, not something fixed in the system.
        </Banner>
      )}

      {data.balances.length === 0 ? (
        <div className="surface-card mb-4">
          <EmptyState
            title="No leave balances yet"
            description="A balance appears the first time leave of that type is accrued, granted or requested. If you have just joined, your first accrual may not have run yet."
          />
        </div>
      ) : (
        <div className="mb-4 grid grid-cols-1 gap-3 tablet:grid-cols-2 desktop:grid-cols-4">
          {data.balances.map((balance) => (
            <BalanceCard
              key={balance.leaveType}
              balance={balance}
              onExplain={() => setLedgerType(balance.leaveType)}
            />
          ))}
        </div>
      )}

      <h2 className="mb-2 text-section font-semibold text-content">Requests</h2>
      <div className="surface-card">
        <DataTable
          rows={data.requests}
          columns={columns}
          rowKey={(row) => row.id}
          testIdPrefix="leave-row"
          emptyState={
            <EmptyState
              title="No leave requested this year"
              description="When you request leave it appears here with its approval status, and you can withdraw it while it is still pending."
              action={
                <Link to="/leave/request">
                  <Button variant="primary">Request leave</Button>
                </Link>
              }
            />
          }
        />
      </div>

      <LedgerDrawer
        employeeId={user?.employeeId ?? null}
        leaveType={ledgerType}
        year={data.year}
        onClose={() => setLedgerType(null)}
      />
    </div>
  );
}

/**
 * A balance with its bar.
 *
 * `pendingDays` is shown as a distinct segment, not folded into "used": a request that
 * has not been approved yet has not consumed the balance, but it will, and someone
 * planning a second trip needs to see both facts.
 */
function BalanceCard({
  balance,
  onExplain,
}: {
  balance: LeaveBalanceView;
  onExplain: () => void;
}): JSX.Element {
  const entitlement = Number(balance.entitledDays) + Number(balance.carriedOverDays);
  const used = Number(balance.usedDays);
  const pending = Number(balance.pendingDays);
  const available = Number(balance.availableDays);
  const denominator = entitlement > 0 ? entitlement : used + pending + available;

  const usedPercent = denominator > 0 ? (used / denominator) * 100 : 0;
  const pendingPercent = denominator > 0 ? (pending / denominator) * 100 : 0;

  return (
    <div className="surface-card p-3" data-testid={`balance-${balance.leaveType}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-body uppercase tracking-[0.04em] text-content-tertiary">{humanise(balance.leaveType)}</p>
        <button
          type="button"
          onClick={onExplain}
          aria-label={`Explain the ${humanise(balance.leaveType)} balance`}
          className="rounded-sm p-0.5 text-content-tertiary hover:text-content"
          data-testid={`explain-${balance.leaveType}`}
        >
          <Info aria-hidden className="h-3.5 w-3.5" />
        </button>
      </div>

      {balance.unlimited === true ? (
        <p className="mt-1 text-title-sm font-semibold text-content">Unlimited</p>
      ) : (
        <>
          <p className="mt-1 text-title-sm font-semibold tabular text-content" data-testid="balance-preview">
            {balance.availableDays}
            <span className="ml-1 text-body font-normal text-content-tertiary">days left</span>
          </p>
          <div
            className="mt-2 flex h-1.5 overflow-hidden rounded-full bg-surface-muted"
            role="img"
            aria-label={`${balance.usedDays} used, ${balance.pendingDays} pending, ${balance.availableDays} available of ${entitlement}`}
          >
            <div className="bg-interactive" style={{ width: `${Math.min(100, usedPercent)}%` }} />
            <div
              className={cn('bg-warning')}
              style={{ width: `${Math.min(100 - usedPercent, pendingPercent)}%` }}
            />
          </div>
          <p className="mt-1.5 text-body text-content-tertiary">
            {balance.usedDays} used
            {pending > 0 && ` · ${balance.pendingDays} pending`}
            {Number(balance.carriedOverDays) > 0 && ` · ${balance.carriedOverDays} carried over`}
            {entitlement > 0 && ` · ${entitlement.toFixed(1)} entitled`}
          </p>
        </>
      )}
    </div>
  );
}
